import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import type { MessageKey } from '../i18n/catalog';
import { useI18n } from '../i18n/I18nContext';

export type GuidedDemoView = 'home' | 'priorities' | 'meals' | 'more';

export const GUIDED_DEMO_TOUR_VERSION = 'frozen-save.guided-tour.v1';

const TOUR_STEPS = [
  {
    id: 'quick-exit',
    view: 'home',
    target: 'quick-exit',
    title: 'demo.tour.step.exit.title',
    description: 'demo.tour.step.exit.description',
  },
  {
    id: 'stock-overview',
    view: 'home',
    target: 'stock-overview',
    title: 'demo.tour.step.stock.title',
    description: 'demo.tour.step.stock.description',
  },
  {
    id: 'priorities',
    view: 'priorities',
    target: 'priorities',
    title: 'demo.tour.step.priorities.title',
    description: 'demo.tour.step.priorities.description',
  },
  {
    id: 'meal-ideas',
    view: 'meals',
    target: 'meal-ideas',
    title: 'demo.tour.step.meals.title',
    description: 'demo.tour.step.meals.description',
  },
  {
    id: 'demo-controls',
    view: 'more',
    target: 'demo-controls',
    title: 'demo.tour.step.reset.title',
    description: 'demo.tour.step.reset.description',
  },
] as const satisfies ReadonlyArray<{
  id: string;
  view: GuidedDemoView;
  target: string;
  title: MessageKey;
  description: MessageKey;
}>;

export const GuidedDemoTour = ({
  onNavigate,
  onClose,
}: {
  onNavigate: (view: GuidedDemoView) => void;
  onClose: () => void;
}) => {
  const { t } = useI18n();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const [stepIndex, setStepIndex] = useState(0);
  const [paused, setPaused] = useState(reducedMotion);
  const panelRef = useRef<HTMLElement>(null);
  const step = TOUR_STEPS[stepIndex]!;
  const isLast = stepIndex === TOUR_STEPS.length - 1;

  useEffect(() => {
    onNavigate(step.view);
    let target: HTMLElement | null = null;
    const timer = window.setTimeout(() => {
      target = document.querySelector<HTMLElement>(`[data-tour-id="${step.target}"]`);
      if (target != null) {
        target.dataset.guidedTourActive = 'true';
        target.setAttribute('aria-describedby', 'guided-demo-tour-description');
        target.scrollIntoView({
          behavior: reducedMotion ? 'auto' : 'smooth',
          block: 'center',
        });
      }
      panelRef.current?.focus({ preventScroll: true });
    }, 80);

    return () => {
      window.clearTimeout(timer);
      target?.removeAttribute('data-guided-tour-active');
      if (target?.getAttribute('aria-describedby') === 'guided-demo-tour-description') {
        target.removeAttribute('aria-describedby');
      }
    };
  }, [onNavigate, reducedMotion, step]);

  useEffect(() => {
    if (paused || isLast) return;
    const timer = window.setTimeout(() => {
      setStepIndex((current) => Math.min(current + 1, TOUR_STEPS.length - 1));
    }, 7_000);
    return () => window.clearTimeout(timer);
  }, [isLast, paused, stepIndex]);

  const previous = () => setStepIndex((current) => Math.max(0, current - 1));
  const next = () => {
    if (isLast) onClose();
    else setStepIndex((current) => current + 1);
  };
  const restart = () => {
    setStepIndex(0);
    setPaused(reducedMotion);
  };
  const handleKeyboard = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      previous();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      next();
    } else if (event.key === ' ') {
      event.preventDefault();
      setPaused((current) => !current);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  };

  return (
    <div className="guided-tour-layer">
      <section
        ref={panelRef}
        className="guided-tour-panel"
        role="dialog"
        aria-labelledby="guided-demo-tour-title"
        aria-describedby="guided-demo-tour-description"
        data-tour-version={GUIDED_DEMO_TOUR_VERSION}
        tabIndex={-1}
        onKeyDown={handleKeyboard}
      >
        <div className="guided-tour-panel__heading">
          <div>
            <span className="eyebrow">{t('demo.tour.role')}</span>
            <h2 id="guided-demo-tour-title">{t(step.title)}</h2>
          </div>
          <button type="button" className="icon-button" onClick={onClose} aria-label={t('demo.tour.close')}>×</button>
        </div>
        <p id="guided-demo-tour-description">{t(step.description)}</p>
        <p className="guided-tour-progress" aria-live="polite">
          {t('demo.tour.progress', { current: stepIndex + 1, total: TOUR_STEPS.length })}
          {reducedMotion ? ` · ${t('demo.tour.reducedMotion')}` : ''}
        </p>
        <div className="guided-tour-controls">
          <button type="button" onClick={restart}>{t('demo.tour.restart')}</button>
          <button type="button" onClick={previous} disabled={stepIndex === 0}>{t('demo.tour.previous')}</button>
          <button type="button" onClick={() => setPaused((current) => !current)}>
            {paused ? t('demo.tour.resume') : t('demo.tour.pause')}
          </button>
          <button type="button" className="guided-tour-controls__primary" onClick={next}>
            {isLast ? t('demo.tour.finish') : t('demo.tour.next')}
          </button>
        </div>
        <p className="guided-tour-keyboard">{t('demo.tour.keyboard')}</p>
      </section>
    </div>
  );
};
